import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faSpinner, 
  faCheck 
} from '@fortawesome/free-solid-svg-icons';
import { getAuth } from "firebase/auth";
import styles from '../ManagerDashboard/Profile.module.css';

const ReportModal = ({ isOpen, onClose, profileData, currentUser }) => {
  const [reportMessage, setReportMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const maxCharCount = 5000;

  const handleMessageChange = (e) => {
    const message = e.target.value;
    if (message.length <= maxCharCount) {
      setReportMessage(message);
      setCharCount(message.length);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!reportMessage.trim()) {
      alert("Please provide details about the report.");
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      // Prepare the report data
      const reportData = {
        reportedUser: {
          userId: profileData.userId,
          userName: profileData.userName,
          fullName: profileData.fullName,
          email: profileData.email,
          userType: profileData.userType,
          userSubType: profileData.userSubType,
        },
        reportingUser: {
          userId: currentUser.userId,
          userName: currentUser.userName,
          fullName: currentUser.fullName,
          email: currentUser.email,
          userType: currentUser.userType,
          userSubType: currentUser.userSubType,
        },
        reportMessage: reportMessage,
        timestamp: new Date().toISOString()
      };
      
      // Get the current user's auth token
      const auth = getAuth();
      const token = await auth.currentUser.getIdToken();
      
      // Send report to Firebase Function
      const response = await fetch('/api/sendReportEmail', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(reportData),
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to send report');
      }
      
      // Report sent successfully
      setSubmitSuccess(true);
      
      // Reset form
      setReportMessage('');
      setCharCount(0);
      
      // Automatically close after success message
      setTimeout(() => {
        setSubmitSuccess(false);
        onClose();
      }, 3000);
      
    } catch (error) {
      console.error('Error submitting report:', error);
      setSubmitError('There was an error submitting your report. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles['modal-overlay']}>
      <div className={styles['modal-content']}>
        <button className={styles['modal-close']} onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
        <h2>Report Profile</h2>
        
        {submitSuccess ? (
          <div className={styles['success-message']}>
            <FontAwesomeIcon icon={faCheck} className={styles['success-icon']} />
            <p>Your report has been submitted successfully. Thank you for helping keep our community safe.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className={styles['report-form']}>
            <p>Please provide details about why you're reporting this profile:</p>
            
            <div className={styles['form-group']}>
              <textarea
                value={reportMessage}
                onChange={handleMessageChange}
                placeholder="Explain the issue in detail..."
                className={styles['report-textarea']}
                rows={8}
                required
              />
              <div className={styles['char-count']}>
                <span className={charCount >= maxCharCount ? styles['char-limit-reached'] : ''}>
                  {charCount}/{maxCharCount}
                </span>
              </div>
            </div>
            
            {submitError && (
              <div className={styles['error-message']}>
                {submitError}
              </div>
            )}
            
            <div className={styles['form-actions']}>
              <button 
                type="button" 
                onClick={onClose}
                className={styles['secondary-button']}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button 
                type="submit"
                className={styles['primary-button']}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className={styles['spin-icon']} />
                    Submitting...
                  </>
                ) : 'Submit Report'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ReportModal;